// src/features/resources/logic.ts
// Framework-agnostic business logic for Resources tab.
// Zero React imports — pure TypeScript functions and data maps.

import type { Resource } from '@core/types/resource';
import type { ResourceGroup, ResourceCardData } from './types';

// ---------------------------------------------------------------------------
// Relation type configuration
// ---------------------------------------------------------------------------

interface RelationTypeInfo {
  labelKey: string;
  icon: string;
  groupOrder: number;
  borderColor: string;
  inverseType: string;
}

export const RELATION_TYPE_CONFIG: Record<string, RelationTypeInfo> = {
  RELATION_TYPE_PARENT:        { labelKey: 'rail.resources.relationType.parent',       icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z',        groupOrder: 2, borderColor: 'border-primary/50',    inverseType: 'RELATION_TYPE_CHILD' },
  RELATION_TYPE_CHILD:         { labelKey: 'rail.resources.relationType.child',        icon: 'M9 5l7 7-7 7',                                                                        groupOrder: 2, borderColor: 'border-primary/50',    inverseType: 'RELATION_TYPE_PARENT' },
  RELATION_TYPE_BLOCKS:        { labelKey: 'rail.resources.relationType.blocks',       icon: 'M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636', groupOrder: 0, borderColor: 'border-destructive', inverseType: 'RELATION_TYPE_BLOCKED_BY' },
  RELATION_TYPE_BLOCKED_BY:    { labelKey: 'rail.resources.relationType.blockedBy',    icon: 'M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636', groupOrder: 0, borderColor: 'border-destructive', inverseType: 'RELATION_TYPE_BLOCKS' },
  RELATION_TYPE_DEPENDS_ON:    { labelKey: 'rail.resources.relationType.dependsOn',    icon: 'M13 10V3L4 14h7v7l9-11h-7z',                                                          groupOrder: 1, borderColor: 'border-yellow-500',   inverseType: 'RELATION_TYPE_DEPENDENCY_OF' },
  RELATION_TYPE_DEPENDENCY_OF: { labelKey: 'rail.resources.relationType.dependencyOf', icon: 'M13 10V3L4 14h7v7l9-11h-7z',                                                          groupOrder: 1, borderColor: 'border-yellow-500',   inverseType: 'RELATION_TYPE_DEPENDS_ON' },
  RELATION_TYPE_ATTACHMENT:    { labelKey: 'rail.resources.relationType.attachment',    icon: 'M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48', groupOrder: 3, borderColor: 'border-muted-foreground/50', inverseType: 'RELATION_TYPE_ATTACHED_TO' },
  RELATION_TYPE_ATTACHED_TO:   { labelKey: 'rail.resources.relationType.attachedTo',   icon: 'M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48', groupOrder: 3, borderColor: 'border-muted-foreground/50', inverseType: 'RELATION_TYPE_ATTACHMENT' },
  RELATION_TYPE_REFERENCES:    { labelKey: 'rail.resources.relationType.references',   icon: 'M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71', groupOrder: 4, borderColor: 'border-primary/30', inverseType: 'RELATION_TYPE_REFERENCED_BY' },
  RELATION_TYPE_REFERENCED_BY: { labelKey: 'rail.resources.relationType.referencedBy', icon: 'M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71', groupOrder: 4, borderColor: 'border-primary/30', inverseType: 'RELATION_TYPE_REFERENCES' },
  RELATION_TYPE_RELATED:       { labelKey: 'rail.resources.relationType.related',      icon: 'M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71', groupOrder: 4, borderColor: 'border-primary/30', inverseType: 'RELATION_TYPE_RELATED' },
  RELATION_TYPE_DUPLICATES:    { labelKey: 'rail.resources.relationType.duplicates',   icon: 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z', groupOrder: 10, borderColor: 'border-muted-foreground/30', inverseType: 'RELATION_TYPE_DUPLICATED_BY' },
  RELATION_TYPE_DUPLICATED_BY: { labelKey: 'rail.resources.relationType.duplicatedBy', icon: 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z', groupOrder: 10, borderColor: 'border-muted-foreground/30', inverseType: 'RELATION_TYPE_DUPLICATES' },
  RELATION_TYPE_CONTEXT:       { labelKey: 'rail.resources.relationType.context',      icon: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', groupOrder: 10, borderColor: 'border-muted-foreground/30', inverseType: 'RELATION_TYPE_CONTEXT' },
  RELATION_TYPE_QUOTE:         { labelKey: 'rail.resources.relationType.quote',        icon: 'M7.5 8.25h9m-9 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z', groupOrder: 10, borderColor: 'border-muted-foreground/30', inverseType: 'RELATION_TYPE_QUOTE' },
  RELATION_TYPE_DISCUSSION:    { labelKey: 'rail.resources.relationType.discussion',   icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z', groupOrder: 10, borderColor: 'border-muted-foreground/30', inverseType: 'RELATION_TYPE_DISCUSSION' },
  RELATION_TYPE_CAUSED_BY:     { labelKey: 'rail.resources.relationType.causedBy',     icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z', groupOrder: 10, borderColor: 'border-yellow-500/50', inverseType: 'RELATION_TYPE_CAUSES' },
  RELATION_TYPE_CAUSES:        { labelKey: 'rail.resources.relationType.causes',       icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z', groupOrder: 10, borderColor: 'border-yellow-500/50', inverseType: 'RELATION_TYPE_CAUSED_BY' },
  RELATION_TYPE_RESOLVES:      { labelKey: 'rail.resources.relationType.resolves',     icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', groupOrder: 10, borderColor: 'border-green-500/50', inverseType: 'RELATION_TYPE_RESOLVED_BY' },
  RELATION_TYPE_RESOLVED_BY:   { labelKey: 'rail.resources.relationType.resolvedBy',   icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', groupOrder: 10, borderColor: 'border-green-500/50', inverseType: 'RELATION_TYPE_RESOLVES' },
};

// ---------------------------------------------------------------------------
// Group configuration
// ---------------------------------------------------------------------------

interface GroupConfig {
  labelKey: string;
  icon: string;
  relationTypes: string[];
  order: number;
}

export const GROUP_CONFIG: Record<string, GroupConfig> = {
  blockers: {
    labelKey: 'rail.resources.group.blockers',
    icon: 'M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636',
    relationTypes: ['RELATION_TYPE_BLOCKS', 'RELATION_TYPE_BLOCKED_BY'],
    order: 0,
  },
  dependencies: {
    labelKey: 'rail.resources.group.dependencies',
    icon: 'M13 10V3L4 14h7v7l9-11h-7z',
    relationTypes: ['RELATION_TYPE_DEPENDS_ON', 'RELATION_TYPE_DEPENDENCY_OF'],
    order: 1,
  },
  hierarchy: {
    labelKey: 'rail.resources.group.hierarchy',
    icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z',
    relationTypes: ['RELATION_TYPE_PARENT', 'RELATION_TYPE_CHILD'],
    order: 2,
  },
  attachments: {
    labelKey: 'rail.resources.group.attachments',
    icon: 'M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48',
    relationTypes: ['RELATION_TYPE_ATTACHMENT', 'RELATION_TYPE_ATTACHED_TO'],
    order: 3,
  },
  references: {
    labelKey: 'rail.resources.group.references',
    icon: 'M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71',
    relationTypes: ['RELATION_TYPE_REFERENCES', 'RELATION_TYPE_REFERENCED_BY', 'RELATION_TYPE_RELATED'],
    order: 4,
  },
  other: {
    labelKey: 'rail.resources.group.other',
    icon: 'M4 6h16M4 12h16M4 18h16',
    relationTypes: [
      'RELATION_TYPE_DUPLICATES', 'RELATION_TYPE_DUPLICATED_BY',
      'RELATION_TYPE_CONTEXT', 'RELATION_TYPE_QUOTE', 'RELATION_TYPE_DISCUSSION',
      'RELATION_TYPE_CAUSED_BY', 'RELATION_TYPE_CAUSES',
      'RELATION_TYPE_RESOLVES', 'RELATION_TYPE_RESOLVED_BY',
    ],
    order: 10,
  },
};

// ---------------------------------------------------------------------------
// Conversion helpers
// ---------------------------------------------------------------------------

export function toResourceCardData(resource: Resource): ResourceCardData {
  return {
    id: resource.entity?.id || '',
    typeName: resource.entity?.type_name || '',
    typeId: resource.entity?.type_id || '',
    urn: resource.urn || '',
    title: resource.title || resource.urn || 'Untitled',
    subtitle: resource.subtitle || '',
    icon: resource.icon || '',
    color: resource.color || '',
    status: resource.status || '',
    statusVariant: mapResourceStatusToVariant(resource.status),
    relationType: resource.relation_type || 'RELATION_TYPE_RELATED',
    linkedAt: resource.linked_at ? String(resource.linked_at) : '',
    linkedByName: resource.linked_by?.display_name || '',
    linkedById: resource.linked_by?.entity?.id,
    isAttachment: ['attachment', 'core.attachment'].includes(resource.entity?.type_name || resource.entity?.type_id || ''),
    productId: resource.entity?.product_id || '',
  };
}

function mapResourceStatusToVariant(status: string | undefined): string {
  switch (status) {
    case 'RESOURCE_STATUS_ACTIVE': return 'default';
    case 'RESOURCE_STATUS_ARCHIVED': return 'secondary';
    case 'RESOURCE_STATUS_DELETED': return 'error';
    case 'RESOURCE_STATUS_DRAFT': return 'secondary';
    case 'RESOURCE_STATUS_PENDING': return 'warning';
    default: return 'default';
  }
}

// ---------------------------------------------------------------------------
// Group resources by relation type
// ---------------------------------------------------------------------------

export function groupResourcesByRelation(resources: Resource[]): ResourceGroup[] {
  const grouped = new Map<string, ResourceCardData[]>();

  for (const resource of resources) {
    const card = toResourceCardData(resource);
    const groupKey = findGroupKey(card.relationType);
    const existing = grouped.get(groupKey) || [];
    existing.push(card);
    grouped.set(groupKey, existing);
  }

  const groups: ResourceGroup[] = [];

  for (const [key, config] of Object.entries(GROUP_CONFIG)) {
    const items = grouped.get(key) || [];
    groups.push({
      relationType: key,
      labelKey: config.labelKey,
      icon: config.icon,
      borderColor: getBorderColorForGroup(key),
      items,
      isCollapsed: false,
      groupOrder: config.order,
    });
  }

  return groups.sort((a, b) => a.groupOrder - b.groupOrder);
}

function findGroupKey(relationType: string): string {
  for (const [key, config] of Object.entries(GROUP_CONFIG)) {
    if (config.relationTypes.includes(relationType)) return key;
  }
  return 'other';
}

function getBorderColorForGroup(groupKey: string): string {
  switch (groupKey) {
    case 'blockers': return 'border-destructive';
    case 'dependencies': return 'border-yellow-500';
    case 'hierarchy': return 'border-primary/50';
    case 'attachments': return 'border-muted-foreground/50';
    case 'references': return 'border-primary/30';
    default: return 'border-muted-foreground/30';
  }
}

// ---------------------------------------------------------------------------
// URN parsing for navigation
// ---------------------------------------------------------------------------

const URN_TYPE_TO_ROUTE: Record<string, string> = {
  'core.task': '/tasks',
  'core.document': '/documents',
  'core.project': '/projects',
  'core.calendar': '/calendars',
  'core.booking': '/bookings',
  'core.team': '/teams',
  'core.conversation': '/chats',
  'core.product': '/products',
  'core.resource': '/resources',
};

const LEGACY_URN_TYPE_TO_ROUTE: Record<string, string> = {
  task: '/tasks',
  document: '/documents',
  project: '/projects',
  calendar: '/calendars',
  booking: '/bookings',
  team: '/teams',
  chat: '/chats',
  conversation: '/chats',
  product: '/products',
  resource: '/resources',
};

function routeForType(typeName?: string, legacyTypeId?: string): string {
  if (typeName) return URN_TYPE_TO_ROUTE[typeName] || LEGACY_URN_TYPE_TO_ROUTE[typeName] || '';
  if (!legacyTypeId) return '';
  return URN_TYPE_TO_ROUTE[legacyTypeId] || LEGACY_URN_TYPE_TO_ROUTE[legacyTypeId] || '';
}

/**
 * Build an in-app route URL for an Entity. Canonical type_name is preferred;
 * slug type_id remains a read-only compatibility path for legacy rows.
 */
export function entityToRoute(
  e: { type_id?: string; type_name?: string; id?: string } | null | undefined,
): string {
  if (!e || !e.id) return '';
  const basePath = routeForType(e.type_name, e.type_id);
  if (!basePath) return '';
  return `${basePath}/${e.id}`;
}

/**
 * Build an in-app route from a Corde URN string and a separately-known
 * entity type. Unknown types remain unroutable.
 */
export function urnToRoute(entityType: string, urn: string): string {
  if (!entityType || !urn) return '';
  const basePath = routeForType(entityType, entityType);
  if (!basePath) return '';
  const id = urn.split(':').pop();
  if (!id) return '';
  return `${basePath}/${id}`;
}

/**
 * Given a URL path that points at one of our internal route prefixes,
 * extract the legacy partial Entity used by the old link-preview flow.
 */
const ROUTE_TO_URN_TYPE: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  for (const [type, prefix] of Object.entries(LEGACY_URN_TYPE_TO_ROUTE)) {
    if (!out[prefix]) out[prefix] = type;
  }
  return out;
})();

export function urlToInternalEntity(rawUrl: string): { type_id: string; id: string } | null {
  if (!rawUrl) return null;
  let pathname = rawUrl;
  try {
    if (/^https?:\/\//i.test(rawUrl)) {
      pathname = new URL(rawUrl).pathname;
    }
  } catch {
    return null;
  }
  if (!pathname.startsWith('/')) return null;
  pathname = pathname.replace(/\/+$/, '');

  // Walk known prefixes longest-first so /chats/new doesn't collide
  // with a hypothetical shorter prefix.
  const sortedPrefixes = Object.keys(ROUTE_TO_URN_TYPE).sort((a, b) => b.length - a.length);
  for (const prefix of sortedPrefixes) {
    if (pathname === prefix) return null; // bare prefix, no id
    if (pathname.startsWith(prefix + '/')) {
      const rest = pathname.slice(prefix.length + 1);
      // Only the first path segment counts as the id; deeper paths
      // like /tasks/abc/comments aren't a single resolvable resource.
      const id = rest.split('/')[0];
      if (!id) return null;
      const type_id = ROUTE_TO_URN_TYPE[prefix];
      return { type_id, id };
    }
  }
  return null;
}

/**
 * Build a Corde URN from an Entity. URN scheme:
 * `urn:corde:{platform}:{product}:{tenant}:{type}:{id}`.
 *
 * Used by features that need to feed an Entity into ResourceCommandMenu
 * or ResourceService.LinkResources without going through the global
 * EntityStore (e.g. linking a chat message to a task — the message is
 * not the "current entity" in the rail).
 *
 * Returns an empty string if any of the URN components are missing.
 */
/**
 * Parse a corde URN back into a wire Entity. The Entity DTO carries no `urn`
 * field, so anything sent as `{urn}` serializes to `{}` — every request that
 * names an entity must send the parsed parts instead.
 */
export function urnToEntity(urn: string | undefined | null): {
  type_id: string; type_name: string; id: string;
  platform_id: string; tenant_id: string; product_id: string;
} | null {
  if (!urn) return null;
  const parts = urn.split(':');
  // urn:corde:{platform}:{product}:{tenant}:{type}:{id}
  if (parts.length < 7 || parts[0] !== 'urn') return null;
  const [, , platformId, productId, tenantId, typeId, id] = parts;
  if (!id || !typeId) return null;
  return {
    type_id: typeId,
    type_name: typeId,
    id,
    platform_id: platformId,
    tenant_id: tenantId,
    product_id: productId,
  };
}

export function entityToUrn(
  e: {
    type_id?: string;
    platform_id?: string;
    product_id?: string;
    tenant_id?: string;
    id?: string;
  } | null | undefined,
): string {
  if (!e || !e.id || !e.type_id) return '';
  return `urn:corde:${e.platform_id ?? ''}:${e.product_id ?? ''}:${e.tenant_id ?? ''}:${e.type_id}:${e.id}`;
}

// ---------------------------------------------------------------------------
// Relation display helpers
// ---------------------------------------------------------------------------

export function getRelationDisplayInfo(relationType: string): { labelKey: string; icon: string; borderColor: string } {
  const config = RELATION_TYPE_CONFIG[relationType];
  if (!config) {
    return {
      labelKey: 'rail.resources.relationType.related',
      icon: 'M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71',
      borderColor: 'border-primary/30',
    };
  }
  return { labelKey: config.labelKey, icon: config.icon, borderColor: config.borderColor };
}

// Cycleable relation types for the Command Menu
export const CYCLEABLE_RELATION_TYPES = [
  'RELATION_TYPE_REFERENCES',
  'RELATION_TYPE_BLOCKS',
  'RELATION_TYPE_ATTACHMENT',
  'RELATION_TYPE_PARENT',
] as const;

export function nextRelationType(current: string): string {
  const idx = CYCLEABLE_RELATION_TYPES.indexOf(current as any);
  if (idx === -1) return CYCLEABLE_RELATION_TYPES[0];
  return CYCLEABLE_RELATION_TYPES[(idx + 1) % CYCLEABLE_RELATION_TYPES.length];
}
